import { Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useOptions } from "../../../contexts/OptionsContext";

type RestrictionsPickerDialogProps = {
  open: boolean;
  /** Restriction ids currently applied to the record. */
  value: number[];
  onClose: () => void;
  /** Called with the confirmed selection. Nothing is sent to the server here — the form's Save does that. */
  onSave: (ids: number[]) => void;
};

/**
 * Pick the restrictions applied to a record. The grid's MultiSelectDialog is bound
 * to DataGrid cell params, so the entity form uses this standalone picker instead.
 */
export default function RestrictionsPickerDialog({ open, value, onClose, onSave }: RestrictionsPickerDialogProps) {
  const { options, loadOptionsForEndpoint } = useOptions();
  const [selected, setSelected] = useState<number[]>(value);
  const [searchTerm, setSearchTerm] = useState("");

  const restrictionOptions = options?.["restrictions"];

  // Start from the record's current selection every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setSelected(value);
    setSearchTerm("");
  }, [open, value]);

  useEffect(() => {
    if (open && !restrictionOptions) {
      loadOptionsForEndpoint("restrictions");
    }
  }, [open, restrictionOptions, loadOptionsForEndpoint]);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (restrictionOptions ?? [])
      .filter((option) => option?.name?.toLowerCase().includes(term))
      .sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""));
  }, [restrictionOptions, searchTerm]);

  const handleToggle = (id: number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: "0.5rem", paddingInline: "1.5rem" } }}>
      <DialogTitle variant='h6' fontWeight='500' sx={{ paddingInline: 0 }}>
        Select Restrictions
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ paddingInline: 0 }}>
        <TextField
          fullWidth
          size='small'
          placeholder='Search...'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
          sx={{ mb: "0.88em", "& fieldset": { borderRadius: "0.5rem" }, "& input": { padding: "0.75rem" } }}
          InputProps={{ startAdornment: <Search /> }}
        />
        {!restrictionOptions ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredOptions.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
            {restrictionOptions.length === 0 ? "No restrictions exist in this organisation yet." : "No restrictions match your search."}
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 400, overflowY: "auto" }} className='scroller'>
            {filteredOptions.map((option) => {
              const id = Number(option.id);
              return (
                <ListItem key={id} disablePadding disableGutters>
                  <ListItemButton onClick={() => handleToggle(id)} sx={{ gap: "0.75em", py: "0.5em" }}>
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <Checkbox checked={selected.includes(id)} disableRipple sx={{ p: 0 }} />
                    </ListItemIcon>
                    <ListItemText primary={option.name} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: "0.75em", "& button": { paddingInline: "1.25em!important" } }}>
        <Button onClick={onClose} variant='secondary-outlined' sx={{ color: "grey.500" }}>
          Cancel
        </Button>
        <Button
          disableElevation
          variant='contained'
          onClick={() => {
            onSave(selected);
            onClose();
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
